import type { BaseServiceOptions, Pagination } from '@/core/types'
import { type TCoil, serviceConfig, moduleServiceCoilHandlers } from '../../..'
import { useNotification, useServiceHandlers } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { toValue } from 'vue'

const { model } = serviceConfig.coils

export const useCoilsQuery = (options: BaseServiceOptions<Pagination>) => {
  const {
    enableQuery,
    modelId,
    modelChildId,
    currentPage = 1,
    filters,
  } = options

  const { errorHttpNotification } = useNotification()

  const serviceHandlers = useServiceHandlers<TCoil>({
    model,
    currentPage: toValue(currentPage) ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const loadRecords = useQuery<TCoil[], AxiosError>({
    queryKey: [modelId, modelChildId],
    queryFn: () => {
      return moduleServiceCoilHandlers.loadCoils({
        parentId: toValue(modelId),
        detailId: toValue(modelChildId),
      })
    },
    onError: (error) => errorHttpNotification(error),
    enabled: enableQuery?.loadRecords,
    refetchOnWindowFocus: false,
  })

  const addRecord = useMutation<TCoil, AxiosError, TCoil>(
    (payload: TCoil) => moduleServiceCoilHandlers.addRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.addRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const editRecord = useMutation<TCoil, AxiosError, TCoil>(
    (payload: TCoil) =>
      moduleServiceCoilHandlers.editRecord(payload, payload.id),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const deleteRecord = useMutation<void, AxiosError, TCoil>(
    (payload) => {
      return moduleServiceCoilHandlers.deleteRecord(payload)
    },
    {
      onSuccess: (_, payload) => serviceHandlers.deleteRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  return {
    loadRecords,
    addRecord,
    editRecord,
    deleteRecord,
  }
}
